import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";

export default function AdminEmails() {
  const [templates, setTemplates] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/email-templates");
    setTemplates(data.templates);
    setDrafts({});
  };
  useEffect(() => { load(); }, []);

  const v = (key, field) => {
    const d = drafts[key] || {};
    if (field in d) return d[field];
    const t = (templates || []).find((x) => x.key === key);
    return t?.[field] ?? "";
  };
  const setV = (key, field, val) => setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: val } }));
  const dirty = (key) => Boolean(drafts[key] && Object.keys(drafts[key]).length);

  const save = async (key) => {
    if (!dirty(key)) return;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/admin/email-templates/${key}`, drafts[key]);
      toast.success("Template enregistré");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };
  const reset = async (key) => {
    try {
      await api.post(`/admin/email-templates/${key}/reset`);
      toast.success("Template restauré aux valeurs par défaut");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!templates) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-emails-page">
      <div className="border-b border-border pb-4">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Email templates</div>
        <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Templates emails</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Variables disponibles : <code className="font-mono">{`{{app_name}} {{user_name}} {{job_name}} {{files_copied}} {{bytes_mb}} {{source}} {{destination}} {{run_url}} {{error_message}}`}</code>
        </p>
      </div>

      <Tabs defaultValue={templates[0]?.key} className="mt-6">
        <TabsList className="rounded-sm flex-wrap h-auto">
          {templates.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="rounded-sm" data-testid={`email-tab-${t.key}`}>
              {t.key}{t.is_overridden && <span className="ml-1 text-[#002FA7]">●</span>}
            </TabsTrigger>
          ))}
        </TabsList>

        {templates.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">Subject (FR)</Label>
                <Input className="mt-1 rounded-sm" value={v(t.key, "subject_fr")} onChange={(e) => setV(t.key, "subject_fr", e.target.value)} data-testid={`email-${t.key}-subject-fr`} />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">Subject (EN)</Label>
                <Input className="mt-1 rounded-sm" value={v(t.key, "subject_en")} onChange={(e) => setV(t.key, "subject_en", e.target.value)} data-testid={`email-${t.key}-subject-en`} />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">Body (FR)</Label>
                <Textarea rows={16} className="mt-1 rounded-sm font-mono text-xs" value={v(t.key, "body_fr")} onChange={(e) => setV(t.key, "body_fr", e.target.value)} data-testid={`email-${t.key}-body-fr`} />
              </div>
              <div>
                <Label className="font-mono text-[11px] uppercase text-muted-foreground">Body (EN)</Label>
                <Textarea rows={16} className="mt-1 rounded-sm font-mono text-xs" value={v(t.key, "body_en")} onChange={(e) => setV(t.key, "body_en", e.target.value)} data-testid={`email-${t.key}-body-en`} />
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => reset(t.key)} className="rounded-sm gap-2" data-testid={`email-${t.key}-reset`}>
                <RotateCcw className="h-4 w-4" /> Restaurer défauts
              </Button>
              <Button disabled={!dirty(t.key) || saving[t.key]} onClick={() => save(t.key)} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid={`email-${t.key}-save`}>
                <Save className="h-4 w-4" /> {saving[t.key] ? "..." : "Enregistrer"}
              </Button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
